'use client';

import { FC, useEffect, useState } from 'react';

import { useAuthentication } from '@ravago/shared/authentication';
import { isFeatureEnabled } from '@ravago/shared/next-data-access';
import { FeatureToggle } from '@ravago/shared/page-data/models/config';
import { AutoSuggestV2Response, BasicFacetWithTermsV2 } from '@ravago/shared/page-data/models/elastic';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { InputGroup } from '@ravago/shared/radiance/components/input-field/components/InputGroup/InputGroup';
import { useTranslations } from '@ravago/shared/translations/client';

import { useSearchParams } from 'next/navigation';

import { AutoSuggestV2 } from '../../../../../shared/auto-suggest/auto-suggest-v2';

interface Props {
   activeFacets: Array<BasicFacetWithTermsV2>;
   language: string;
   globals: RendererGlobals;
   autoSuggestResult?: AutoSuggestV2Response;
   loading?: boolean;
   autoSuggestExpanded?: boolean;
   numberOfFacets?: number;
   baseRoutes?: {
      product: string;
      brand: string;
      caseStudy: string;
   };
   onFilterFlyoutOpen?: () => void;
   onFacetsChange?: (filters: Array<BasicFacetWithTermsV2>) => void;
   onSearchChange?: (value: string) => void;
   onSearch?: (value: string) => void;
   onAutoSuggestMenuChange?: (value: boolean) => void;
   featureToggles: FeatureToggle[];
}

export const ProductPortfolioSearchV2: FC<Props> = ({
   activeFacets,
   language,
   globals,
   autoSuggestResult,
   loading = false,
   autoSuggestExpanded,
   numberOfFacets = 0,
   baseRoutes,
   onFilterFlyoutOpen,
   onFacetsChange,
   onSearchChange,
   onSearch,
   onAutoSuggestMenuChange,
   featureToggles
}) => {
   const translate = useTranslations();
   const searchParams = useSearchParams();
   const initialSearch = searchParams.get('search') ?? '';

   const { accessToken } = useAuthentication();

   const [hasAutoSuggestFeatureToggle, setHasAutoSuggestFeatureToggle] = useState<boolean>(true);
   const [searchValue, setSearchValue] = useState(initialSearch);

   const handleSearchChange = (value: string) => {
      setSearchValue(value);
      onSearchChange?.(value);
   };

   const handleSearch = (value: string) => {
      onSearch?.(value);
   };

   const handleClear = () => {
      setSearchValue('');
      onSearchChange?.('');
      onSearch?.('');
   };

   const handleApplyFacets = (facets: Array<BasicFacetWithTermsV2>) => {
      facets.forEach((facet) => {
         const existingFacet = activeFacets.find((activeFacet) => activeFacet.facetName === facet.facetName);

         if (existingFacet) {
            existingFacet.terms.push(...facet.terms.filter((term) => !existingFacet.terms.includes(term)));
         } else {
            activeFacets.push(facet);
         }

         onFacetsChange?.(activeFacets);
      });
   };

   useEffect(() => {
      if (!accessToken) return;

      const checkFeatureToggle = async () => {
         setHasAutoSuggestFeatureToggle(isFeatureEnabled(featureToggles, 'auto-suggest'));
      };

      checkFeatureToggle();
   }, [accessToken]);

   return (
      <Box gap="sm">
         {hasAutoSuggestFeatureToggle ? (
            <AutoSuggestV2
               id="product-portfolio-search"
               language={language}
               loading={loading}
               result={autoSuggestResult}
               baseRoutes={baseRoutes}
               globals={globals}
               searchTerm={initialSearch}
               onChange={handleSearchChange}
               onSearch={handleSearch}
               onClear={handleClear}
               expanded={autoSuggestExpanded}
               onAutoSuggestMenuChange={onAutoSuggestMenuChange}
               onFacetsClick={handleApplyFacets}
            />
         ) : (
            <InputGroup
               id="product-portfolio-search"
               actionIcon="search"
               autocomplete="off"
               clearable
               label={translate('products-search-placeholder')}
               onChange={handleSearchChange}
               onEnter={handleSearch}
               onActionClick={handleSearch}
               onClear={handleClear}
               value={searchValue}
               width="100%"
            />
         )}

         <BreakpointSwitch excludeViews={['desktop']}>
            <IconButton
               icon="sliders-h"
               variant="on-light"
               badge={numberOfFacets !== 0 ? numberOfFacets.toString() : undefined}
               onClick={onFilterFlyoutOpen}
            />
         </BreakpointSwitch>
      </Box>
   );
};
